import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { SessionListResponse, statesStrToStates } from 'interfaces/sessions';
import { SessionV2Columns, ReviewStatusColumns, PROSPECT_INFO_COLUMNS, SessionAgentsColumns, SessionStatesColumns, SESSION_METRICS_COLUMNS } from 'interfaces/table';
import { AgentType, PartyRole, Platform } from 'interfaces/db';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { fromReduxSafeSessionDataResult, sessionDetailsSlice } from 'lib/redux/sessions/data/data-slice';
import { updateSessionMetricInList } from '../../lib/redux/sessions/lists/list-slice';
import { SelectedTranscript } from './SelectedTranscript';
import { SessionDetailView, viewSupportsCustomScores } from 'components/History/views';
import { Maximize2, Minimize2, X, FileText, Share2, Globe, Webhook } from 'lucide-react';
import { SessionDetails } from '../SessionDetails/SessionDetails';
import { SessionStatus } from '../SessionStatus/SessionStatus';
import { WebhookPreviewModal } from '../SessionDetails/WebhookPreviewModal';
import {
  isSessionMissed,
  isSessionAnswered,
  isSessionProspectAnswered,
  isSessionGatekeeperAnswered,
  getSessionDisposition,
  getSessionSentiment,
  getSessionObjectionString,
  getSessionCallDuration,
  formatCallDuration,
  getSessionSummary,
  getSessionCallTime,
  getSessionUserId,
  getSessionState,
  getSessionNameFromState,
  getPlatformUrlFromSession,
  getPlatformFromSession,
} from '../../utils/session';
import { Typography } from '../../interfaces/typography';
import { CustomScoresModal } from '../CustomScores/CustomScoresModal';
import { useCustomScoresModal } from '../../hooks/useCustomScoresModal';
import { SessionCustomScoresSummary } from '../../lib/redux/sessions/custom-scores/session-list-custom-scores-hook';
import { useUserData } from '../../lib/redux/users/user-data-hook';
import { useTeamAccountType } from '../../lib/redux/users/team-account-type-hook';
import { getServicesManager } from '../../services';
import { useColumnOptions } from '../../lib/redux/filters/options/options-hook';
import { ClickableMetric } from '../SessionDetails/ClickableMetric';
import { canUserEditSessionMetrics } from '../../utils/session';


interface TranscriptPreviewProps {
  session: SessionListResponse;
  isSelected: boolean;
  onSelect?: () => void;
  onUnselect?: () => void;
  onExpand?: () => void;
  view: SessionDetailView;
  customScores?: SessionCustomScoresSummary | null;
}

const styles = {
  transcriptItem: {
    padding: '12px 16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'all 0.15s ease-in-out',
    cursor: 'pointer',
  },
  selectedItem: {
    backgroundColor: '#e0f7fa',
  },
  notAnsweredItem: {
    backgroundColor: '#fafafa',
    color: '#888',
  },
  liveAnsweredItem: (isSelected: boolean) => ({
    backgroundColor: '#f8faff',
    borderLeft: isSelected ? 'none' : '2px solid #3b82f6',
  }),
  callIcon: {
    marginRight: '8px',
    color: '#4CAF50',
  },
  callInfo: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 500,
    gap: '8px',
    width: '100%',
    justifyContent: 'space-between',
  },
  callMainInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  callActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginLeft: 'auto',
  },
  callDuration: {
    fontSize: '13px',
    color: '#666',
  },
  agentName: {
    color: '#666',
    fontSize: '13px',
  },
  transcriptPreview: {
    fontSize: '13px',
    color: '#555',
    lineHeight: '1.4',
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '4px',
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
  },
  noContactTag: {
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
  },
  missedTag: {
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
  },
  prospectTag: {
    backgroundColor: '#e3f5e9',
    color: '#1f7d43',
  },
  gatekeeperTag: {
    backgroundColor: '#fff3cd',
    color: '#856404',
  },
  livePersonTag: {
    backgroundColor: '#e6f2ff',
    color: '#0070dd',
  },
  userInitials: {
    width: '28px',
    height: '28px',
    backgroundColor: '#e3f2fd',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 500,
    color: '#1976d2',
    cursor: 'pointer',
    position: 'relative' as const,
  },
  userTooltip: {
    position: 'absolute' as const,
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginTop: '4px',
    padding: '4px 8px',
    backgroundColor: '#1f2937',
    color: 'white',
    borderRadius: '4px',
    fontSize: '12px',
    whiteSpace: 'nowrap' as const,
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.2s, visibility 0.2s',
    zIndex: 50,
  },
  userInitialsHover: {
    '&:hover': {
      backgroundColor: '#bbdefb',
    },
    '&:hover > div': {
      opacity: 1,
      visibility: 'visible',
    },
  },
  expandButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    borderRadius: '4px',
    color: '#666',
    transition: 'all 0.2s ease',
    position: 'relative' as const,
  },
  tooltip: {
    position: 'absolute' as const,
    bottom: '-8px',
    right: '0',
    transform: 'translateY(100%)',
    backgroundColor: '#1f2937',
    color: 'white',
    padding: '6px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    whiteSpace: 'nowrap' as const,
    opacity: 0,
    visibility: 'hidden',
    transition: 'all 0.2s ease',
    zIndex: 50,
  },
  livePersonItem: (isSelected: boolean) => ({
    backgroundColor: '#f8faff',
    borderLeft: isSelected ? 'none' : '2px solid #3b82f6',
  }),
  missedCallItem: {
    backgroundColor: '#fef2f2', // Light red background (red-50)
  },
};

// Platform logo mapping
const PLATFORM_TO_LOGO: Partial<Record<Platform, string>> = {
  [Platform.HUBSPOT]: "/assets/hubspot-logo.svg",
  [Platform.SALESLOFT]: "/assets/salesloft-logo.png",
  [Platform.OUTREACH]: "/assets/outreach-logo.svg",
  [Platform.APOLLO]: "/assets/apollo.jpeg",
  [Platform.SALESFORCE]: "/assets/salesforce.svg",
  [Platform.ZOHO]: "/assets/zoho.svg",
  [Platform.AMPLEMARKET]: "/assets/amplemarket.svg",
  [Platform.GOHIGHLEVEL]: "/assets/gohighlevel.svg",
};

export const TranscriptPreview: React.FC<TranscriptPreviewProps> = ({ 
  session, 
  isSelected, 
  onSelect,
  onUnselect,
  onExpand,
  view,
  customScores,
}) => {
  const [isFullyExpanded, setIsFullyExpanded] = useState(false);
  const [showTranscriptCopied, setShowTranscriptCopied] = useState(false);
  const [showLinkCopied, setShowLinkCopied] = useState(false);
  const [showPublicLinkCopied, setShowPublicLinkCopied] = useState(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [isSummaryTruncated, setIsSummaryTruncated] = useState(false);
  const [isSessionStateExpanded, setIsSessionStateExpanded] = useState(false);
  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);
  const [selectedWebhookId, setSelectedWebhookId] = useState<string | undefined>(undefined);
  // Custom scores modal management
  const {
    isCustomScoresModalOpen,
    modalAnchorRect,
    showModal,
    hideModal,
    cancelHideModal,
    startHideModal,
    closeModal
  } = useCustomScoresModal();
  const summaryRef = useRef<HTMLDivElement>(null);
  const customScoresRef = useRef<HTMLDivElement>(null);
  
  // Only subscribe to session details when selected to reduce memory usage
  const sessionDetailsDataRaw = useSelector((state: RootState) => 
    isSelected ? state.sessionDetails.sessionIdToDetails[session.session_id]?.data : undefined
  );
  
  // Convert Redux-safe data back to original format
  const sessionDetailsData = useMemo(() => {
    return sessionDetailsDataRaw ? fromReduxSafeSessionDataResult(sessionDetailsDataRaw) : undefined;
  }, [sessionDetailsDataRaw]);
  
  // Clear any lingering state when component unmounts
  useEffect(() => {
    return () => {
      // Clear any modals or temporary state
      if (isCustomScoresModalOpen) {
        closeModal();
      }
    };
  }, [isCustomScoresModalOpen, closeModal]);

  // Get user from Redux for session status
  const user = useSelector((state: RootState) => state.user.value);

  // Get webhooks from Redux to show domain info
  const { webhooks } = useSelector((state: RootState) => state.payloadManagement.sessionWebhooks);

  // Get visible accounts from user data hook
  const { visibleAccounts, isAdmin, canProvision } = useUserData();

  // Get column options for dispositions, sentiments, and purposes
  const { getColumnOptions } = useColumnOptions();
  const dispatch = useDispatch();

  // Check if user can edit this session's metrics
  const canEdit = canUserEditSessionMetrics(session, user?.user_id, isAdmin);

  // Get options for each metric type
  const dispositionOptions = getColumnOptions(SESSION_METRICS_COLUMNS.USER_DISPOSITION);
  const sentimentOptions = getColumnOptions(SESSION_METRICS_COLUMNS.USER_SENTIMENT);
  const purposeOptions = getColumnOptions(SESSION_METRICS_COLUMNS.USER_PURPOSE);

  // Handler for updating session metrics
  const handleMetricUpdate = useCallback(async (
    metricType: 'disposition' | 'sentiment' | 'purpose',
    value: string | null
  ) => {
    // First update the Redux store optimistically
    const sessionId = session.session_id;
    const updates: any = {};
    
    switch (metricType) {
      case 'disposition':
        updates[SESSION_METRICS_COLUMNS.USER_DISPOSITION] = value;
        break;
      case 'sentiment':
        updates[SESSION_METRICS_COLUMNS.USER_SENTIMENT] = value;
        break;
      case 'purpose':
        updates[SESSION_METRICS_COLUMNS.USER_PURPOSE] = value;
        break;
    }

    dispatch(sessionDetailsSlice.actions.updateSessionMetric({
      sessionId,
      sessionMetric: updates
    }));

    // Also update the session in the lists
    dispatch(updateSessionMetricInList({
      sessionId,
      updates
    }));

    // Then call the API
    try {
      const servicesManager = getServicesManager();
      const apiUpdates: any = {};

      if (metricType === 'disposition') apiUpdates.userDisposition = value;
      if (metricType === 'sentiment') apiUpdates.userSentiment = value;
      if (metricType === 'purpose') apiUpdates.userPurpose = value;

      await servicesManager.updateSessionMetrics(sessionId, apiUpdates);
    } catch (error) {
      // TODO: Consider reverting the optimistic update or showing an error message
    }
  }, [session.session_id, dispatch]);

  // Get user info
  const agentUserInfo = useMemo(() => {
    // Try the new smart user ID logic first
    let userId = getSessionUserId(session);
    
    // Fall back to legacy RESOURCE_ID if smart logic doesn't return a result
    if (!userId) {
      userId = session.result.get(SessionV2Columns.RESOURCE_ID);
    }
    
    if (!userId || !visibleAccounts?.users) return null;
    
    const user = visibleAccounts.users.find(u => u.user_id === userId);
    if (!user) return null;
    
    const initials = user.user_name.split(' ').map(n => n[0]).join('').toUpperCase();
    return {
      name: user.user_name,
      initials
    };
  }, [session, visibleAccounts]);

  const userInfo = useMemo(() => {
    if (agentUserInfo) return agentUserInfo
    const isBot = session.result.get(SessionAgentsColumns.AGENT_TYPES_STR)?.includes(AgentType.VOICE_BOT)
    if (isBot) {
      return {
        name: 'Voice Bot',
        initials: 'VB'
      };
    }
    return null
  }, [agentUserInfo]);

  // Color generation for disposition/sentiment tags
  const getTagColors = useCallback((value: string, isDisposition: boolean = true) => {
    const stringToColor = (str: string) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      const h = hash % 360;
      return h;
    };

    const hue = stringToColor(value);
    const bg = `hsl(${hue}, 70%, 92%)`;
    const text = `hsl(${hue}, 70%, 30%)`;
    
    return { bg, text };
  }, []);

  const handleCopyTranscript = useCallback(async () => {
    if (!sessionDetailsData?.turns) return;
    
    try {
      // Format each turn into text
      const transcriptText = sessionDetailsData.turns
        .filter(turn => turn.text.trim() !== '') // Skip empty turns
        .map(turn => {
          const speaker = turn.role === PartyRole.REP ? 'Agent' : 'Prospect';
          return `${speaker}: ${turn.text.trim()}`;
        })
        .join('\n');

      // Add header with call details
      const prospectName = sessionDetailsData.prospect_info?.[PROSPECT_INFO_COLUMNS.PROSPECT_NAME] || 'Unknown Prospect';
      const companyName = sessionDetailsData.prospect_info?.[PROSPECT_INFO_COLUMNS.COMPANY_NAME];
      
      const headerParts = [
        `Call with ${prospectName}`,
        `Date: ${sessionDetailsData.session.started_at.toLocaleString('en-US', { 
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: true
        })}`,
      ];

      if (companyName) {
        headerParts.push(`Company: ${companyName}`);
      }

      const fullTranscript = `${headerParts.join('\n')}\n\n${transcriptText}`;
      
      await navigator.clipboard.writeText(fullTranscript);
      setShowTranscriptCopied(true);
      setTimeout(() => setShowTranscriptCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy transcript:', err);
    }
  }, [sessionDetailsData]);

  const handleCopyLink = useCallback(async () => {
    try {
      const url = `https://${window.location.hostname}/transcripts?id=${session.session_id}`;
      await navigator.clipboard.writeText(url);
      setShowLinkCopied(true);
      setTimeout(() => setShowLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  }, [session.session_id]);

  const handleCopyPublicLink = useCallback(async () => {
    try {
      const servicesManager = getServicesManager();
      const accessToken = await servicesManager.getSessionAccessToken(session.session_id);

      if (accessToken?.session_access_token) {
        const url = `https://${window.location.hostname}/view?access_token=${accessToken.session_access_token}&session_id=${session.session_id}`;
        await navigator.clipboard.writeText(url);
        setShowPublicLinkCopied(true);
        setTimeout(() => setShowPublicLinkCopied(false), 2000);
      } else {
        console.error('Failed to generate access token');
      }
    } catch (err) {
      console.error('Failed to copy public URL:', err);
    }
  }, [session.session_id]);

  const isCallAnswered = isSessionAnswered(session);
  const isGatekeeper = isSessionGatekeeperAnswered(session);
  const isProspect = isSessionProspectAnswered(session);

  const disposition = getSessionDisposition(session);
  const sentiment = getSessionSentiment(session);
  const objectionString = getSessionObjectionString(session);

  const summary = getSessionSummary(session);

  // Get platform info and URL
  const platformInfo = useMemo(() => {
    const platform = getPlatformFromSession(session, sessionDetailsData);
    const url = getPlatformUrlFromSession(session, sessionDetailsData);

    if (!platform || !url) return null;

    const logo = PLATFORM_TO_LOGO[platform];

    return {
      platform,
      url,
      logo
    };
  }, [session, sessionDetailsData]);
    
  // Check if summary is truncated
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;
    
    if (summaryRef.current && summary && isCallAnswered) {
      // Small delay to ensure layout is complete
      timeoutId = setTimeout(() => {
        const element = summaryRef.current;
        if (element) {
          // Check if the scrollHeight is greater than clientHeight (indicating truncation)
          const isTextTruncated = element.scrollHeight > element.clientHeight;
          setIsSummaryTruncated(isTextTruncated);
        }
      }, 50);
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [summary, isCallAnswered, isSelected]);

  // Reset expansion state when selection changes
  useEffect(() => {
    setIsSummaryExpanded(false);
  }, [isSelected]);


  // Get session state fields for rendering (filtered)
  const sessionStateFields = useMemo(() => {
    let allFields: [string, string][] = [];

    // First try to get from session details (when selected)
    if (sessionDetailsData?.session_state?.states) {
      allFields = Object.entries(sessionDetailsData.session_state.states);
    } else {
      // Fall back to session list data (if available)
      const states = getSessionState(session);
      if (Object.keys(states).length > 0) {
        allFields = Object.entries(states);
      }
    }

    // Filter out fields that are too long, contain 'summary' in the key name, or are webhook status fields
    return allFields.filter(([key, value]) => {
      const keyLower = key.toLowerCase();
      const isSummaryField = keyLower.includes('summary');
      const isTooLong = value.length > 100;
      const isWebhookField = key.startsWith('__webhook_') && key.endsWith('__');
      return !isSummaryField && !isTooLong && !isWebhookField;
    });
  }, [sessionDetailsData?.session_state?.states, session]);

  const shouldShowSessionStateExpand = sessionStateFields.length > 5;
  const visibleSessionStateFields = isSessionStateExpanded ? sessionStateFields : sessionStateFields.slice(0, 5);
  const hiddenFieldsCount = sessionStateFields.length - 5;

  // Check if session has webhooks by looking for webhook status fields in state
  const webhookInfo = useMemo(() => {
    // Check if this is a bot call
    const isBot = session.result.get(SessionAgentsColumns.AGENT_TYPES_STR)?.includes(AgentType.VOICE_BOT);
    if (!isBot) return [];

    // Get session state
    const states = sessionDetailsData?.session_state?.states || getSessionState(session);

    // Look for webhook status fields (pattern: __webhook_{webhook_id}__)
    const webhookKeys = Object.keys(states).filter(key => key.startsWith('__webhook_') && key.endsWith('__'));

    if (webhookKeys.length === 0) return [];

    // Extract webhook info for ALL webhooks
    return webhookKeys.map(webhookKey => {
      const match = webhookKey.match(/__webhook_(.+)__/);
      const webhookId = match ? match[1] : webhookKey.replace('__webhook_', '').replace(/__$/, '');

      // Find webhook in Redux to get URL
      const webhook = webhooks.find(w => w.webhook_id === webhookId);
      let domain = '';
      if (webhook?.url) {
        try {
          const url = new URL(webhook.url);
          domain = url.hostname;
        } catch (e) {
          // If URL parsing fails, just use empty domain
          domain = '';
        }
      }

      return {
        webhookId,
        status: states[webhookKey],
        domain
      };
    });
  }, [session, sessionDetailsData, webhooks]);

  // Get styling for the container based on answered status and tab
  const isMissed = isSessionMissed(session);
  const containerStyle = {
    ...styles.transcriptItem,
    ...(isSelected ? styles.selectedItem : {}),
    ...(isMissed ? styles.missedCallItem : {}),
    ...(!isCallAnswered ? styles.notAnsweredItem : {}),
    ...(isCallAnswered ? styles.liveAnsweredItem(isSelected) : {}),
  };

  return (
    <div>
      {isFullyExpanded && isSelected ? (
        <div className="fixed inset-0 z-50 bg-white">
          <div className="absolute right-4 bottom-4 z-50">
            <button
              onClick={() => setIsFullyExpanded(false)}
              className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
            >
              <Minimize2 size={20} />
            </button>
          </div>
          <SessionDetails
            sessionId={session.session_id}
            view={view}
            inView={true}
          />
        </div>
      ) : (
        <div className={`
          relative overflow-hidden transition-all duration-300 ease-in-out
          ${isSelected 
            ? 'ring-2 ring-blue-500 shadow-md rounded-lg bg-blue-50' 
            : 'hover:bg-gray-50 hover:shadow-sm rounded-lg'
          }
        `}>
          <div 
            style={containerStyle}
            onClick={() => onSelect?.()}
            className={`
              group transition-all duration-150 ease-in-out flex flex-col
              ${isCallAnswered ? 'gap-1' : 'gap-1.5'}
              ${isSelected ? 'bg-blue-50 rounded-t-lg' : 'rounded-lg'}
            `}
          >
            <div style={styles.callInfo}>
              <div style={styles.callMainInfo}>
                <span className="text-[13px]">📞</span>
                <span>{getSessionCallTime(session)}</span>
                {userInfo && (
                  <>
                    <span style={styles.agentName}>•</span>
                    <span style={styles.agentName}>Agent: {userInfo.name}</span>
                  </>
                )}
                
                {/* Show inline status for unanswered calls */}
                {!isCallAnswered && (
                  <>
                    <span style={styles.agentName}>•</span>
                    {isMissed ? (
                      <span style={{
                        ...styles.tag,
                        margin: 0,
                        backgroundColor: '#fee2e2', // Light red background
                        color: '#dc2626' // Red text
                      }}>
                        missed
                      </span>
                    ) : (
                      <span style={{...styles.tag, ...styles.noContactTag, margin: 0}}>
                        no contact
                      </span>
                    )}

                    {/* Show disposition inline for unanswered calls */}
                    {disposition && !isMissed && !sentiment && (
                      <>
                        <span style={styles.agentName}>•</span>
                        <ClickableMetric
                          value={disposition}
                          options={dispositionOptions}
                          placeholder=""
                          onSelect={(value) => handleMetricUpdate('disposition', value)}
                          disabled={!canEdit}
                          className="text-xs inline"
                        />
                      </>
                    )}

                  </>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                {/* Platform logo link - show if available */}
                {platformInfo && (
                  <a
                    href={platformInfo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100 transition-colors"
                    aria-label={`View in ${platformInfo.platform}`}
                  >
                    {platformInfo.logo ? (
                      <img
                        src={platformInfo.logo}
                        alt={platformInfo.platform}
                        className="w-4 h-4 object-contain"
                      />
                    ) : (
                      <span className="text-xs text-gray-600">🔗</span>
                    )}
                  </a>
                )}

                {/* Session Status - always show on the right */}
                {user?.user_id && (
                  <SessionStatus
                    isMainView={true}
                    sessionId={session.session_id}
                    sessionUserId={getSessionUserId(session) || session.result.get(SessionV2Columns.RESOURCE_ID) || ''}
                    type={'sessionList'}
                    viewerUserId={user.user_id}
                    reviewIsOpen={sessionDetailsData?.review_status ? sessionDetailsData.review_status.is_open : session.result.get(ReviewStatusColumns.IS_OPEN)}
                    reviewOpenedByUser={sessionDetailsData?.review_status ? (sessionDetailsData.review_status.opening_user_id === user.user_id) : (session.result.get(ReviewStatusColumns.OPENING_USER_ID) === user.user_id)}
                  />
                )}
                
                {/* Action buttons group - when selected */}
                {isSelected && (
                  <div className="flex items-center gap-0.5 px-1 py-0.5 bg-gray-50 rounded border border-gray-200">
                    {/* Webhook button - show if bot call with webhooks */}
                    {webhookInfo && (
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsWebhookModalOpen(true);
                          }}
                          className="group/webhook relative flex items-center justify-center w-5 h-5 hover:bg-white hover:shadow-sm rounded transition-colors"
                          aria-label="View webhook"
                        >
                          <Webhook className="w-3 h-3 text-gray-600 group-hover/webhook:text-gray-800" />
                          <span className="absolute bottom-0 right-0 translate-y-[120%] opacity-0 invisible group-hover/webhook:opacity-100 group-hover/webhook:visible mb-1 bg-gray-900 text-white text-xs py-1 px-1.5 rounded shadow-lg whitespace-nowrap z-10">
                            View webhook
                          </span>
                        </button>
                      </div>
                    )}

                    {/* Copy transcript button */}
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyTranscript();
                        }}
                        className="group/copy relative flex items-center justify-center w-5 h-5 hover:bg-white hover:shadow-sm rounded transition-colors"
                        aria-label="Copy transcript"
                      >
                        <FileText className="w-3 h-3 text-gray-600 group-hover/copy:text-gray-800" />
                        <span className="absolute bottom-0 right-0 translate-y-[120%] opacity-0 invisible group-hover/copy:opacity-100 group-hover/copy:visible mb-1 bg-gray-900 text-white text-xs py-1 px-1.5 rounded shadow-lg whitespace-nowrap z-10">
                          Copy transcript
                        </span>
                      </button>
                      
                      {/* Copy notification */}
                      {showTranscriptCopied && (
                        <div className="absolute bottom-0 right-0 translate-y-[120%] mb-1 bg-gray-900 text-white text-xs py-1 px-1.5 rounded shadow-lg whitespace-nowrap z-10">
                          Transcript copied!
                        </div>
                      )}
                    </div>

                    {/* Share button */}
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyLink();
                        }}
                        className="group/share relative flex items-center justify-center w-5 h-5 hover:bg-white hover:shadow-sm rounded transition-colors"
                        aria-label="Share transcript"
                      >
                        <Share2 className="w-3 h-3 text-gray-600 group-hover/share:text-gray-800" />
                        <span className="absolute bottom-0 right-0 translate-y-[120%] opacity-0 invisible group-hover/share:opacity-100 group-hover/share:visible mb-1 bg-gray-900 text-white text-xs py-1 px-1.5 rounded shadow-lg whitespace-nowrap z-10">
                          Copy link
                        </span>
                      </button>
                      
                      {/* Copy notification */}
                      {showLinkCopied && (
                        <div className="absolute bottom-0 right-0 translate-y-[120%] mb-1 bg-gray-900 text-white text-xs py-1 px-1.5 rounded shadow-lg whitespace-nowrap z-10">
                          Link copied!
                        </div>
                      )}
                    </div>

                    {/* Public Share button - Only show for admins */}
                    {isAdmin && (
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyPublicLink();
                          }}
                          className="group/public relative flex items-center justify-center w-5 h-5 hover:bg-white hover:shadow-sm rounded transition-colors"
                          aria-label="Share publicly"
                        >
                          <Globe className="w-3 h-3 text-gray-600 group-hover/public:text-gray-800" />
                          <span className="absolute bottom-0 right-0 translate-y-[120%] opacity-0 invisible group-hover/public:opacity-100 group-hover/public:visible mb-1 bg-gray-900 text-white text-xs py-1 px-1.5 rounded shadow-lg whitespace-nowrap z-10">
                            Share publicly
                          </span>
                        </button>

                        {/* Copy notification */}
                        {showPublicLinkCopied && (
                          <div className="absolute bottom-0 right-0 translate-y-[120%] mb-1 bg-gray-900 text-white text-xs py-1 px-1.5 rounded shadow-lg whitespace-nowrap z-10">
                            Public link copied!
                          </div>
                        )}
                      </div>
                    )}

                    {/* Expand button */}
                    {onExpand && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onExpand();
                          setIsFullyExpanded(true);
                        }}
                        className="group/expand relative flex items-center justify-center w-5 h-5 hover:bg-white hover:shadow-sm rounded transition-colors"
                        aria-label="View full session"
                      >
                        <Maximize2 className="w-3 h-3 text-gray-600 group-hover/expand:text-gray-800" />
                        <span className="absolute bottom-0 right-0 translate-y-[120%] opacity-0 invisible group-hover/expand:opacity-100 group-hover/expand:visible mb-1 bg-gray-900 text-white text-xs py-1 px-1.5 rounded shadow-lg whitespace-nowrap z-10">
                          View full session
                        </span>
                      </button>
                    )}

                    {/* Close button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onUnselect?.();
                      }}
                      className="flex items-center justify-center w-5 h-5 hover:bg-white hover:shadow-sm rounded transition-colors"
                      aria-label="Unselect session"
                    >
                      <X className="w-3 h-3 text-gray-600" />
                    </button>
                  </div>
                )}

                {/* Duration - always aligned */}
                <div className="flex items-center justify-center w-10">
                  <span className="text-xs text-gray-500 tabular-nums">
                    {formatCallDuration(getSessionCallDuration(session))}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex justify-between items-start">
              {/* Summary section - show for answered calls */}
              {isCallAnswered === true && summary !== undefined && summary !== "" && (
                <div className="flex-1">
                  <div 
                    ref={summaryRef}
                    className="overflow-hidden transition-all duration-300 ease-in-out"
                    style={{
                      ...styles.transcriptPreview,
                      display: '-webkit-box',
                      WebkitLineClamp: isSummaryExpanded ? 'unset' : 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: '1.4',
                      maxHeight: isSummaryExpanded ? 'none' : '2.8em', // Fallback: 2 lines * 1.4 line-height
                    }}
                  >
                    {summary}
                  </div>
                  
                  {/* Show more/less button */}
                  {isSummaryTruncated && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsSummaryExpanded(!isSummaryExpanded);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 transition-colors mt-1 font-medium"
                    >
                      {isSummaryExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {/* Tags container - show for answered calls, unanswered calls with sentiment, or session states (for provision users only) */}
            {(isCallAnswered || (!isCallAnswered && sentiment)) || (canProvision && sessionStateFields.length > 0) ? (
              <div style={styles.tagsContainer}>
                {isCallAnswered ? (
                  <>
                    {/* Answered call status tags */}
                    {isProspect && (
                      <span style={{...styles.tag, ...styles.prospectTag}}>
                        prospect
                      </span>
                    )}
                    {!isProspect && isGatekeeper && (
                      <span style={{...styles.tag, ...styles.gatekeeperTag}}>
                        gatekeeper
                      </span>
                    )}
                    {!isProspect && !isGatekeeper && (
                      <span style={{...styles.tag, ...styles.livePersonTag}}>
                        live person
                      </span>
                    )}
                    
                    {/* Objections for answered calls */}
                    {objectionString && (
                      <span style={{
                        ...styles.tag,
                        backgroundColor: '#fee2e2',
                        color: '#dc2626'
                      }}>
                        {objectionString}
                      </span>
                    )}
                    
                    {/* Show clickable metrics for answered calls - show if options exist or value is set */}
                    {(dispositionOptions.length > 0 || disposition) && (
                      <ClickableMetric
                        value={disposition}
                        options={dispositionOptions}
                        placeholder="Set disposition"
                        onSelect={(value) => handleMetricUpdate('disposition', value)}
                        disabled={!canEdit}
                        className="text-xs"
                      />
                    )}

                    {(sentimentOptions.length > 0 || sentiment) && (
                      <ClickableMetric
                        value={sentiment}
                        options={sentimentOptions}
                        placeholder="Set sentiment"
                        onSelect={(value) => handleMetricUpdate('sentiment', value)}
                        disabled={!canEdit}
                        className="text-xs"
                      />
                    )}

                    {(purposeOptions.length > 0 || sessionDetailsData?.sales_metric?.user_purpose) && (
                      <ClickableMetric
                        value={sessionDetailsData?.sales_metric?.user_purpose || null}
                        options={purposeOptions}
                        placeholder="Set purpose"
                        onSelect={(value) => handleMetricUpdate('purpose', value)}
                        disabled={!canEdit}
                        className="text-xs"
                      />
                    )}
                    
                    {/* Custom Scores for answered calls (only in main/analytics views) */}
                    {customScores && customScores.hasCustomScores && viewSupportsCustomScores(view) && (
                      <div 
                        ref={customScoresRef} 
                        style={{ 
                          display: 'inline-flex', 
                          flexWrap: 'wrap', 
                          gap: '4px',
                          maxWidth: '100%'
                        }}
                      >
                        {customScores.scoresSummary.slice(0, isSelected ? 1 : 2).map((score, index) => (
                          <span 
                            key={`custom-${index}`}
                            style={{
                              ...styles.tag,
                              backgroundColor: score.backgroundColor,
                              color: score.textColor,
                              fontSize: '10px',
                              fontWeight: 600,
                              cursor: 'help',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={`${score.label}: ${score.displayValue}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              showModal(session.session_id, rect);
                            }}
                            onMouseEnter={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              showModal(session.session_id, rect);
                            }}
                            onMouseLeave={hideModal}
                          >
                            {score.label}: {score.displayValue}
                          </span>
                        ))}
                        {customScores.customScoresCount > (isSelected ? 1 : 2) && (
                          <span 
                            style={{
                              ...styles.tag,
                              backgroundColor: '#faf5ff',
                              color: '#7c3aed',
                              border: '1px solid #e9d5ff',
                              fontSize: '10px',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              showModal(session.session_id, rect);
                            }}
                            onMouseEnter={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              showModal(session.session_id, rect);
                            }}
                            onMouseLeave={hideModal}
                          >
                            +{customScores.customScoresCount - (isSelected ? 1 : 2)} more
                          </span>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  /* For unanswered calls, show clickable metrics - show if options exist or value is set */
                  <>
                    {(dispositionOptions.length > 0 || disposition) && (
                      <ClickableMetric
                        value={disposition}
                        options={dispositionOptions}
                        placeholder="Set disposition"
                        onSelect={(value) => handleMetricUpdate('disposition', value)}
                        disabled={!canEdit}
                        className="text-xs"
                      />
                    )}

                    {(sentimentOptions.length > 0 || sentiment) && (
                      <ClickableMetric
                        value={sentiment}
                        options={sentimentOptions}
                        placeholder="Set sentiment"
                        onSelect={(value) => handleMetricUpdate('sentiment', value)}
                        disabled={!canEdit}
                        className="text-xs"
                      />
                    )}

                    {(purposeOptions.length > 0 || sessionDetailsData?.sales_metric?.user_purpose) && (
                      <ClickableMetric
                        value={sessionDetailsData?.sales_metric?.user_purpose || null}
                        options={purposeOptions}
                        placeholder="Set purpose"
                        onSelect={(value) => handleMetricUpdate('purpose', value)}
                        disabled={!canEdit}
                        className="text-xs"
                      />
                    )}
                    
                    {/* Custom Scores for unanswered calls (only in main/analytics views) */}
                    {customScores && customScores.hasCustomScores && viewSupportsCustomScores(view) && (
                      <div style={{ 
                        display: 'inline-flex', 
                        flexWrap: 'wrap', 
                        gap: '4px',
                        maxWidth: '100%'
                      }}>
                        {customScores.scoresSummary.slice(0, isSelected ? 1 : 2).map((score, index) => (
                          <span 
                            key={`custom-${index}`}
                            style={{
                              ...styles.tag,
                              backgroundColor: score.backgroundColor,
                              color: score.textColor,
                              fontSize: '10px',
                              fontWeight: 600,
                              cursor: 'help',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={`${score.label}: ${score.displayValue}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              showModal(session.session_id, rect);
                            }}
                            onMouseEnter={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              showModal(session.session_id, rect);
                            }}
                            onMouseLeave={hideModal}
                          >
                            {score.label}: {score.displayValue}
                          </span>
                        ))}
                        {customScores.customScoresCount > (isSelected ? 1 : 2) && (
                          <span 
                            style={{
                              ...styles.tag,
                              backgroundColor: '#faf5ff',
                              color: '#7c3aed',
                              border: '1px solid #e9d5ff',
                              fontSize: '10px',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              showModal(session.session_id, rect);
                            }}
                            onMouseEnter={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              showModal(session.session_id, rect);
                            }}
                            onMouseLeave={hideModal}
                          >
                            +{customScores.customScoresCount - (isSelected ? 1 : 2)} more
                          </span>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Webhook badges - show if bot call with webhooks */}
                {webhookInfo.map((info, idx) => (
                  <button
                    key={`webhook-${idx}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedWebhookId(info.webhookId);
                      setIsWebhookModalOpen(true);
                    }}
                    style={{
                      ...styles.tag,
                      backgroundColor: parseInt(info.status) >= 200 && parseInt(info.status) < 300
                        ? '#d1fae5'
                        : parseInt(info.status) >= 300 && parseInt(info.status) < 400
                        ? '#fef3c7'
                        : '#fee2e2',
                      color: parseInt(info.status) >= 200 && parseInt(info.status) < 300
                        ? '#065f46'
                        : parseInt(info.status) >= 300 && parseInt(info.status) < 400
                        ? '#92400e'
                        : '#991b1b',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    className="hover:opacity-80 transition-opacity"
                  >
                    🔗 {info.status}{info.domain ? `: ${info.domain}` : ''}
                  </button>
                ))}

                {/* Session State fields - show if available and user can provision */}
                {canProvision && visibleSessionStateFields.map(([key, value]) => (
                  <div 
                    key={key}
                    style={{
                      ...styles.tag,
                      backgroundColor: '#e0f2fe',
                      color: '#0c4a6e',
                      border: '1px solid #bae6fd',
                      padding: '2px 6px'
                    }}
                  >
                    <Typography variant="smallParagraph" fontWeight="500" color="#0c4a6e">
                      {`${key}: ${value}`}
                    </Typography>
                  </div>
                ))}
                
                {/* Expand/collapse button for session state - show if needed and user can provision */}
                {canProvision && shouldShowSessionStateExpand && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSessionStateExpanded(!isSessionStateExpanded);
                    }}
                    style={{
                      ...styles.tag,
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      border: '1px solid #93c5fd',
                      cursor: 'pointer',
                      padding: '2px 6px'
                    }}
                    className="hover:bg-blue-200 transition-colors"
                  >
                    <Typography variant="smallParagraph" fontWeight="500" color="#1e40af">
                      {isSessionStateExpanded ? `- show less` : `+${hiddenFieldsCount} more`}
                    </Typography>
                  </button>
                )}
              </div>
            ) : null}
          </div>

          {/* Transcript Section */}
          {isSelected && !isFullyExpanded && (
            <SelectedTranscript
              sessionId={session.session_id}
              view={view}
              onUnselect={onUnselect}
            />
          )}
        </div>
      )}
      
      {/* Custom Scores Modal */}
      {session && (
        <CustomScoresModal
          session={session}
          isOpen={isCustomScoresModalOpen}
          onClose={closeModal}
          anchorRect={modalAnchorRect}
          position="auto"
          onMouseEnter={cancelHideModal}
          onMouseLeave={startHideModal}
      />
      )}

      {/* Webhook Preview Modal */}
      {webhookInfo.length > 0 && (
        <WebhookPreviewModal
          sessionId={session.session_id}
          webhookId={selectedWebhookId}
          isOpen={isWebhookModalOpen}
          onClose={() => {
            setIsWebhookModalOpen(false);
            setSelectedWebhookId(undefined);
          }}
        />
      )}
    </div>
  );
};
